/**
 * Dynamic Page Loader - Public & User Pages Only
 * 
 * Replaces static import.meta.glob with dynamic imports based on bundle configuration.
 * This enables proper code splitting and prevents circular dependencies in production.
 * 
 * SECURITY: Admin pages are NOT listed here. They are in separate protected loaders
 * that are only imported when the user has admin access. This prevents exposing
 * admin page names to unauthenticated users or regular users viewing source.
 * 
 * Instead of pre-loading all pages at build time with import.meta.glob,
 * this system creates dynamic import functions that are only evaluated when the page is requested.
 */

/**
 * Public & User Pages - accessible to all authenticated users
 * Admin pages are in separate loaders for security
 */
const PAGE_IMPORTS = {
    // Public bundle pages - visible to all
    'login': () => import('../pages/login.tag'),
    'trial-signup': () => import('../pages/trial-signup.tag'),
    'trial-success': () => import('../pages/trial-success.tag'),
    'forgot-password': () => import('../pages/forgot-password.tag'),
    'reset-password': () => import('../pages/reset-password.tag'),
    'password-setup': () => import('../pages/password-setup.tag'),
    'password-change': () => import('../pages/password-change.tag'),
    'verify-email': () => import('../pages/verify-email.tag'),
    'verify-email-change': () => import('../pages/verify-email-change.tag'),
    'link-account': () => import('../pages/link-account.tag'),
    'sso-consent': () => import('../pages/sso-consent.tag'),
    // METALARK-835: cross-customer invitation acceptance lives in the
    // public bundle because the email link lands here BEFORE the user
    // signs in. The page handles the auth gate itself — preview is
    // anonymous; the accept/decline POSTs route through login when
    // the user isn't yet authenticated.
    'accept-invitation': () => import('../pages/accept-invitation.tag'),
    'support': () => import('../pages/support.tag'),
    'support-setup-guide': () => import('../pages/support-setup-guide.tag'),
    'support-user-guide': () => import('../pages/support-user-guide.tag'),
    'support-lms-integration': () => import('../pages/support-lms-integration.tag'),
    'support-architecture': () => import('../pages/support-architecture.tag'),
    'support-release-notes': () => import('../pages/support-release-notes.tag'),
    'support-external-player': () => import('../pages/support-external-player.tag'),
    'support-lms-portability': () => import('../pages/support-lms-portability.tag'),
    'not-found': () => import('../pages/not-found.tag'),
    'onboarding': () => import('../pages/onboarding.tag'),
    
    // User bundle pages - accessible to all authenticated users
    'home': () => import('../pages/home.tag'),
    'reports': () => import('../pages/reports.tag'),
    'report-preview': () => import('../pages/report-preview.tag'),
    'content': () => import('../pages/content.tag'),
    'deep-search': () => import('../pages/deep-search.tag'),
    'keywords': () => import('../pages/keywords.tag'),
    'upload': () => import('../pages/upload.tag'),
    'skills': () => import('../pages/skills.tag'),
    'skill': () => import('../pages/skill.tag'),
    'customer-tag': () => import('../pages/customer-tag.tag'),
    'collection': () => import('../pages/collection.tag'),
    'item': () => import('../pages/item.tag'),
    'summary': () => import('../pages/summary.tag'),
    'test-generation': () => import('../pages/test-generation.tag'),
    'collection-test-generation': () => import('../pages/collection-test-generation.tag'),
    'settings': () => import('../pages/settings.tag'),
    'export': () => import('../pages/export.tag'),
    'select-workspace': () => import('../pages/select-workspace.tag'),
    'billing': () => import('../pages/billing.tag'),
    'credits': () => import('../pages/credits.tag'),
    'migration': () => import('../pages/migration.tag'),
    'bulk-edit': () => import('../pages/bulk-edit.tag'),
    // METALARK-835: signed-in surface for pending invitations. The
    // accept-invitation page handles the single-token email-link flow
    // (public); this list lives in the user bundle so any authenticated
    // user can discover invites addressed to them without the email.
    'invitations': () => import('../pages/invitations.tag'),
};

/**
 * Get a page import function by name
 * 
 * @param {string} pageName - Name of the page (without '-page' suffix)
 * @param {Object} options - Optional options for bundle validation
 * @returns {Function|null} Import function or null if not found
 */
export function getPageImport(pageName, options = {}) {
    const importFn = PAGE_IMPORTS[pageName];
    
    if (!importFn) {
        console.warn(`[PageLoader] Page not found: ${pageName}`);
        return null;
    }
    
    return importFn;
}

/**
 * Get all available public/user pages as a module-like object
 * Admin pages are NOT included for security - use bundleLoader to get admin pages
 * 
 * @returns {Object} Object mapping page paths to import functions
 */
export function getAllPageModules() {
    const result = {};
    
    for (const [pageName, importFn] of Object.entries(PAGE_IMPORTS)) {
        // Map internal format to glob-like paths for compatibility
        const pagePath = `./pages/${pageName}.tag`;
        result[pagePath] = importFn;
    }
    
    console.log(`[PageLoader] getAllPageModules() returning ${Object.keys(result).length} public/user pages`);
    return result;
}

/**
 * Get pages for public or user bundle only
 * For admin bundles, use bundleLoader which dynamically loads admin page loaders
 * 
 * @param {string} bundleName - Name of the bundle ('public' or 'user')
 * @returns {Object} Object mapping page paths to import functions for that bundle
 */
export function getPagesByBundle(bundleName) {
    if (bundleName === 'public') {
        // 14 public pages
        return Object.fromEntries(
            Object.entries(PAGE_IMPORTS)
                .filter(([name]) => [
                    'login', 'trial-signup', 'trial-success', 'forgot-password',
                    'reset-password', 'password-setup', 'password-change', 'verify-email',
                    'verify-email-change', 'link-account', 'sso-consent', 'accept-invitation',
                    'support', 'not-found', 'onboarding'
                ].includes(name))
                .map(([name, fn]) => [`./pages/${name}.tag`, fn])
        );
    } else if (bundleName === 'user') {
        // 18 user pages
        return Object.fromEntries(
            Object.entries(PAGE_IMPORTS)
                .filter(([name]) => ![
                    'login', 'trial-signup', 'trial-success', 'forgot-password',
                    'reset-password', 'password-setup', 'password-change', 'verify-email',
                    'verify-email-change', 'link-account', 'sso-consent', 'accept-invitation',
                    'support', 'not-found', 'onboarding'
                ].includes(name))
                .map(([name, fn]) => [`./pages/${name}.tag`, fn])
        );
    } else if (bundleName === 'customer-admin' || bundleName === 'system-admin') {
        // Admin pages - will be loaded dynamically by bundleLoader
        console.warn(`[PageLoader] Admin pages for ${bundleName} not in main loader - use bundleLoader`);
        return {};
    } else {
        console.error(`[PageLoader] Unknown bundle: ${bundleName}`);
        return {};
    }
}

/**
 * Validate that a module matches expected structure
 * 
 * @param {Object} module - Module to validate
 * @returns {boolean} True if module has default export
 */
export function isValidPageModule(module) {
    return module && typeof module === 'object' && 'default' in module;
}
